import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import consultationRestDayService, { formatDateFr } from '../../../services/consultationRestDayService';
import { useDoctorWorkspace } from '../doctorWorkspaceContext';
import { doctorConsultationEditPath } from '../doctorPaths';
import { HeadDoctorPageHeader } from '../../head-doctor/ui';
import { DOCTOR_COPY } from '../doctorCopy';

export default function DoctorRestDaysPage({ isHeadDoctor = false }) {
  const { workSiteId, portalPaths } = useDoctorWorkspace();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('ISSUED');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (workSiteId) params.site_id = workSiteId;
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await consultationRestDayService.list(params);
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.message || 'Chargement impossible');
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [workSiteId, statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="recv-dash-pro--main pb-4">
      <HeadDoctorPageHeader
        badge={isHeadDoctor ? 'Médecin chef' : DOCTOR_COPY.PHYSICIAN_DASHBOARD.BADGE_PHYSICIAN}
        title="Jours de repos"
        description="Certificats médicaux d'arrêt émis depuis les consultations — filtres par site de travail."
      />

      {!workSiteId ? (
        <Alert variant="warning">Sélectionnez un site de travail en barre supérieure.</Alert>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Form.Select
          size="sm"
          style={{ maxWidth: 180 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Tous les statuts</option>
          <option value="ISSUED">Émis</option>
          <option value="CANCELLED">Annulés</option>
        </Form.Select>
        <Form.Control
          size="sm"
          type="search"
          placeholder="Patient ou motif…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 240 }}
        />
        <Button size="sm" variant="outline-secondary" onClick={load}>
          Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <Table responsive hover size="sm" className="bg-white rounded shadow-sm">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Période</th>
              <th>Durée</th>
              <th>Médecin</th>
              <th>Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  Aucun certificat trouvé.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.patient_name || '—'}</strong>
                    <div className="small text-muted">Consultation #{r.consultation_id}</div>
                  </td>
                  <td className="small">
                    {formatDateFr(r.start_date)}
                    <br />
                    <span className="text-muted">→ {formatDateFr(r.end_date)}</span>
                  </td>
                  <td>
                    {r.days_count} j
                  </td>
                  <td className="small">{r.doctor_name || '—'}</td>
                  <td>
                    <Badge bg={r.status === 'ISSUED' ? 'success' : 'secondary'}>
                      {r.status === 'ISSUED' ? 'Émis' : 'Annulé'}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button
                      as={Link}
                      to={doctorConsultationEditPath(r.consultation_id, portalPaths)}
                      size="sm"
                      variant="outline-primary"
                    >
                      Ouvrir
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
}
